import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE, PaginateConfig } from './pagination.model';

@Component({
	selector: 'app-pagination',
	templateUrl: './pagination.component.html',
	styleUrls: ['./pagination.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginationComponent implements OnChanges {
	@Input() public itemsCount = FIRST_PAGE;
	@Input() public currentPage = FIRST_PAGE;
	@Input() public pageSize = DEFAULT_PAGE_SIZE;

	@Output() public configChanged: EventEmitter<PaginateConfig> = new EventEmitter<PaginateConfig>();

	public countOfPages = FIRST_PAGE;
	public previousIsDisabled = false;
	public nextIsDisabled = false;
	public readonly firstPage = FIRST_PAGE;
	public readonly form: FormGroup = this.initForm();
	private lastEmittedConfig: PaginateConfig | null = null;

	public ngOnChanges(changes: SimpleChanges): void {
		const isItemsCountChanged = changes.itemsCount?.currentValue !== changes.itemsCount?.previousValue;

		if (isItemsCountChanged) {
			this.calculateCountOfPages();
			this.form
				.get('pageNumber')
				.setValidators([Validators.required, Validators.min(FIRST_PAGE), Validators.max(this.countOfPages)]);
		}
		this.form.get('pageNumber').patchValue(changes.currentPage?.currentValue);
		this.previousIsDisabled = this.currentPage === FIRST_PAGE;
		this.nextIsDisabled = this.currentPage === this.countOfPages;
	}

	public changePage(pageNumber: number): void {
		this.form.get('pageNumber').patchValue(pageNumber);
		this.emitConfig({ pageNumber, pageSize: this.pageSize });
	}

	public applyPage($event?: Event): void {
		$event?.stopImmediatePropagation();
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}
		const { pageNumber } = this.form.value;
		const normalizedValue = Math.round(pageNumber);
		if (this.currentPage === normalizedValue) {
			return;
		}
		this.emitConfig({ pageNumber: normalizedValue, pageSize: this.pageSize });
	}

	private emitConfig(configChange: PaginateConfig): void {
		if (!this.lastEmittedConfig) {
			this.lastEmittedConfig = configChange;
			this.configChanged.emit(configChange);
			return;
		}
		const { pageSize: lastPageSize, pageNumber: lastPageNumber } = this.lastEmittedConfig;
		const { pageSize: nextPageSize, pageNumber: nextPageNumber } = configChange;
		if (lastPageSize !== nextPageSize || lastPageNumber !== nextPageNumber) {
			this.lastEmittedConfig = configChange;
			this.configChanged.emit(configChange);
			return;
		}
	}

	private calculateCountOfPages(): void {
		this.countOfPages = Math.ceil(this.itemsCount / this.pageSize);
	}

	private initForm(): FormGroup {
		return new FormGroup({
			pageNumber: new FormControl(FIRST_PAGE, [
				Validators.required,
				Validators.min(FIRST_PAGE),
				Validators.max(FIRST_PAGE),
			]),
		});
	}
}
